import { FilterOutlined } from '@ant-design/icons';
import { IssueCaseStatusCode } from '@gooduncles/gu-app-schema';
import { Button, Checkbox, DatePicker, Popover, Space } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';

import { issueCaseStatusBadgeMap } from 'src/utils/issue/issue-util';

import classes from './TableToolbar.module.scss';

const { RangePicker } = DatePicker;

interface StatusFilterButtonProps<T> {
  label: string;
  options: { label: string; value: T }[];
  value: T[];
  onChange: (values: T[]) => void;
}

const StatusFilterButton = <T,>({ label, options, value, onChange }: StatusFilterButtonProps<T>) => {
  const content = (
    <Checkbox.Group
      className={classes.filterCheckboxGroup}
      value={value as string[]}
      onChange={(checkedValues) => onChange(checkedValues as T[])}
    >
      <Space direction='vertical' size={4}>
        {options.map((option) => (
          <Checkbox key={String(option.value)} value={option.value}>
            {option.label}
          </Checkbox>
        ))}
      </Space>
    </Checkbox.Group>
  );

  return (
    <Popover content={content} trigger='click' placement='bottomLeft'>
      <Button icon={<FilterOutlined />}>{label}</Button>
    </Popover>
  );
};

interface TableToolbarProps {
  dateRange: [Date, Date];
  onDateRangeChange: (dates: [Date, Date]) => void;
  issueStatuses: IssueCaseStatusCode[];
  onIssueStatusesChange: (statuses: IssueCaseStatusCode[]) => void;
}

const issueStatusOptions = [
  IssueCaseStatusCode.PENDING,
  IssueCaseStatusCode.OPEN,
  IssueCaseStatusCode.IN_PROGRESS,
  IssueCaseStatusCode.CLOSED,
  IssueCaseStatusCode.CANCELED,
].map((status) => ({
  label: issueCaseStatusBadgeMap[status].text,
  value: status,
}));

const TableToolbar: FC<TableToolbarProps> = ({
  dateRange,
  onDateRangeChange,
  issueStatuses,
  onIssueStatusesChange,
}) => {
  const dateRangeForPicker: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs(dateRange[0]), dayjs(dateRange[1])];

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onDateRangeChange([dates[0].toDate(), dates[1].toDate()]);
    }
  };

  return (
    <Space size='middle' wrap className={classes.toolbar}>
      <Space size='small'>
        <span>기간:</span>
        <RangePicker value={dateRangeForPicker} onChange={handleDateRangeChange} allowClear={false} />
      </Space>
      <StatusFilterButton<IssueCaseStatusCode>
        label='이슈 상태'
        options={issueStatusOptions}
        value={issueStatuses}
        onChange={onIssueStatusesChange}
      />
    </Space>
  );
};

export default TableToolbar;
